"use client";

import * as React from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, ChevronRight, Check, Loader2 } from "lucide-react";
import { Avatar } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useVisitConsumables } from "@/hooks/modal/useVisitConsumables";
import { useCurrencySymbol } from "@/hooks/shared/useCurrencySymbol";
import { AttachmentChip } from "@/components/custom/attachments/AttachmentChip";
import { AttachmentViewer } from "@/components/custom/attachments/AttachmentViewer";
import type { AttachmentItem } from "@/components/custom/attachments/types";
import { deleteAttachment } from "@/lib/services/attachmentsService";

export type VisitHistoryPanelStatus = "waiting" | "arrived" | "not-arrived" | "confirmed" | "cancelled";

export interface VisitHistoryPanelProps {
  visitId: number;
  date: string; // "08.10"
  time: string; // "16:30"
  employeeName: string;
  employeeAvatar?: string;
  services: { id: number; name: string; cost: number; paid: number }[];
  status: VisitHistoryPanelStatus;
  location?: string;
  comment?: string;
  /** The appointment's photos and videos (boona docs/codebase/attachments/) */
  attachments?: AttachmentItem[];
}

const statusConfig: Record<VisitHistoryPanelStatus, { bg: string; dot: string; labelKey: string; labelDefault: string }> = {
  waiting: { bg: "bg-amber-50", dot: "bg-amber-400", labelKey: "appointment.status.waiting", labelDefault: "Waiting" },
  confirmed: { bg: "bg-indigo-50", dot: "bg-indigo-500", labelKey: "appointment.status.confirmed", labelDefault: "Confirmed" },
  arrived: { bg: "bg-green-50", dot: "bg-green-500", labelKey: "appointment.status.arrived", labelDefault: "Arrived" },
  "not-arrived": { bg: "bg-rose-50", dot: "bg-rose-500", labelKey: "appointment.status.no_show", labelDefault: "No show" },
  cancelled: { bg: "bg-gray-50", dot: "bg-gray-400", labelKey: "appointment.visit.cancelled", labelDefault: "Cancelled" },
};

export function VisitHistoryPanelMobile({
  visitId,
  date,
  time,
  employeeName,
  employeeAvatar,
  services,
  status,
  location,
  comment,
  attachments,
}: VisitHistoryPanelProps) {
  const [open, setOpen] = React.useState(false);
  const [showConsumables, setShowConsumables] = React.useState(false);
  const { t } = useTranslation();
  const [files, setFiles] = React.useState<AttachmentItem[]>(attachments ?? []);
  const [viewer, setViewer] = React.useState<number | null>(null);
  React.useEffect(() => { setFiles(attachments ?? []); }, [attachments]);
  const currencySymbol = useCurrencySymbol();
  const sc = statusConfig[status];

  // Only fetch when expanded and user clicked "Details"
  const canShowDetails = status === "arrived";
  const { data: consumables, isLoading: consumablesLoading, error: consumablesError } = useVisitConsumables(
    visitId,
    canShowDetails && showConsumables
  );

  return (
    <div className="rounded-2xl bg-white ring-1 ring-gray-200 shadow-sm p-4">
      {/* Header */}
      <button
        type="button"
        className="w-full flex items-center gap-3 text-left"
        onClick={() => setOpen((v) => !v)}
      >
        <Avatar className="h-10 w-10 shrink-0">
          {employeeAvatar ? (
            // Avatar accepts children; mirror project pattern with img
            // keep simple img to avoid depending on AvatarImage export
            // eslint-disable-next-line @next/next/no-img-element
            <img src={employeeAvatar} alt={employeeName} className="w-full h-full rounded-full object-cover" />
          ) : (
            <div className="bg-gray-200 w-full h-full rounded-full flex items-center justify-center text-xs">
              {employeeName?.slice(0, 2).toUpperCase()}
            </div>
          )}
        </Avatar>

        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between">
            <div className="truncate font-medium text-[15px] text-gray-900">{employeeName}</div>
            <ChevronDown className={cn("h-5 w-5 text-gray-500 transition-transform", open && "rotate-180")} />
          </div>
          <div className="mt-0.5 flex items-center gap-2 text-[13px] text-gray-500">
            <span className="truncate">{date} • {time}</span>
            <span className={cn("ml-auto inline-flex items-center gap-2 rounded-full px-2 py-0.5 text-[12px]", sc.bg)}>
              <span className={cn("inline-block h-1.5 w-1.5 rounded-full", sc.dot)} />
              {t(sc.labelKey, sc.labelDefault)}
            </span>
          </div>
        </div>
      </button>


      {/* Details */}
      {open && (
        <div className="mt-4 space-y-3">
          <div className="space-y-2">
            {services.map((s, i) => (
              <div key={i} className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-2 min-w-0">
                  <div className="mt-1 rounded-full p-0.5 ring-1 ring-gray-200">
                    <Check className="h-3.5 w-3.5 text-gray-500" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-[13px] text-gray-900">{s.name}</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-[12px] text-gray-500">{t("appointment.payment.paid", "paid")} {s.paid.toLocaleString("ru-RU")}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Details link for consumables */}
          <div className="pt-1">
            {canShowDetails ? (
              <Button
                variant="link"
                size="sm"
                className="p-0 h-auto text-blue-600 text-[13px]"
                onClick={() => setShowConsumables((v) => !v)}
              >
                {t("appointment.history.details", "Details")} <ChevronRight className={cn("h-3 w-3 ml-1 transition-transform", showConsumables && "rotate-90")} />
              </Button>
            ) : (
              <span className="text-gray-400 text-[13px] cursor-not-allowed">
                {t("appointment.history.details", "Details")}
              </span>
            )}
          </div>

          {/* Consumables display */}
          {canShowDetails && showConsumables && (
            <div className="rounded-xl bg-blue-50 ring-1 ring-blue-100 px-3 py-2 space-y-1.5">
              {consumablesLoading && (
                <div className="flex items-center text-[12px] text-gray-500">
                  <Loader2 className="h-3 w-3 animate-spin mr-2" />
                  {t("appointment.history.loading_consumables", "Loading consumables...")}
                </div>
              )}
              {consumablesError && (
                <div className="text-[12px] text-red-500">
                  {t("appointment.history.consumables_error", "Error loading consumables")}
                </div>
              )}
              {!consumablesLoading && !consumablesError && consumables && consumables.length === 0 && (
                <div className="text-[12px] text-gray-400">
                  {t("appointment.history.no_consumables", "No consumables")}
                </div>
              )}
              {!consumablesLoading && !consumablesError && consumables && consumables.length > 0 && (
                <div className="space-y-2">
                  {(() => {
                    // Group consumables by service_id
                    const consumablesByService = consumables.reduce((acc, item) => {
                      const serviceId = item.service_id
                      if (!acc[serviceId]) {
                        acc[serviceId] = []
                      }
                      acc[serviceId].push(item)
                      return acc
                    }, {} as Record<number, typeof consumables>)

                    return services.map((service) => {
                      const serviceConsumables = consumablesByService[service.id]
                      if (!serviceConsumables || serviceConsumables.length === 0) {
                        return null
                      }
                      return (
                        <div key={service.id} className="space-y-1">
                          <div className="text-[11px] font-medium text-gray-600">
                            {service.name}
                          </div>
                          {serviceConsumables.map((item, index) => (
                            <div key={index} className="text-[12px] text-gray-700 pl-2">
                              • {item.title}, {item.amount} {item.unit}, {(item.price * item.amount).toFixed(2)} {currencySymbol}
                            </div>
                          ))}
                        </div>
                      )
                    })
                  })()}
                </div>
              )}
            </div>
          )}

          {(location || comment || !!files.length) && (
            <div className="rounded-xl bg-gray-50 ring-1 ring-gray-200 px-3 py-2 space-y-1.5">
              {location && <div className="text-[12px] text-gray-700">{t("appointment.details.location", "Location:")} {location}</div>}
              {comment && <div className="text-[12px] text-gray-700">{t("appointment.details.comment", "Comment:")} {comment}</div>}
              {!!files.length && <AttachmentChip items={files} onOpen={(i) => setViewer(i)} />}
            </div>
          )}
          <AttachmentViewer
            items={files}
            index={viewer}
            onIndexChange={setViewer}
            onDelete={viewer !== null && files[viewer]?.canDelete ? async (item) => {
              if (!window.confirm(t("attachments.delete_confirm", "Delete this file? It can't be undone."))) return
              await deleteAttachment(Number(item.id))
              const left = files.filter((x) => x.id !== item.id)
              setFiles(left)
              setViewer(left.length ? Math.min(viewer, left.length - 1) : null)
            } : undefined}
          />
        </div>
      )}
    </div>
  );
}
